import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Navbar, Nav, Container, Button } from 'react-bootstrap';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { Company } from '../services/api';
import { isDemoAccount } from '../config/demoAccounts';

const Navigation: React.FC = () => {
  const { isAuthenticated, logout, userId: authUserId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState<Company | null>(null);

  // ?view=public — used by "Preview as visitor" so a logged-in user can see
  // exactly what an anonymous visitor sees in the chrome (no Admin Dashboard
  // link, no Logout button), without actually logging out the session.
  const viewAsPublic = searchParams.get('view') === 'public';
  const showAuthChrome = isAuthenticated && !viewAsPublic;
  const exitPreview = () => {
    searchParams.delete('view');
    setSearchParams(searchParams);
  };

  // Extract userId from URL if it exists (e.g., /userId/something)
  const getUserIdFromPath = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    // Check if first part looks like a userId (not standard routes like 'admin', 'login', etc.)
    if (pathParts.length > 0 && !['admin', 'login', 'embed', 'case-study'].includes(pathParts[0])) {
      return pathParts[0];
    }
    return undefined;
  };

  // Prioritize userId from auth context when logged in, otherwise use URL-based detection
  const userId = isAuthenticated && authUserId ? authUserId : getUserIdFromPath();

  // For anonymous users (no userId), we'll use the default company configuration
  // by calling api.getCompany() without userId parameter

  const loadCompany = useCallback(async () => {
    try {
      const companyData = await api.getCompany(userId);
      setCompany(companyData);
    } catch (error) {
      console.error('Failed to load company data:', error);
    }
  }, [userId]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  // Publish the navbar's live height as a CSS variable so layouts below (e.g. MapView) can
  // size themselves to fit the remaining viewport without hardcoding a breakpoint-dependent value.
  const navbarRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = navbarRef.current;
    if (!el) return;
    const apply = (h: number) => {
      document.documentElement.style.setProperty('--app-nav-height', `${h}px`);
    };
    apply(el.offsetHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [company]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Clicking "View Full Map" should always reset the map to the fit-all-markers view,
  // including when the user is already on the map page. Pushing a fresh nonce in location
  // state lets MapView re-run its fit-bounds effect even when the URL is unchanged.
  const mapTarget = userId ? `/${userId}` : '/';
  const handleViewFullMap = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(mapTarget, { state: { fitNonce: Date.now() } });
  };

  const isLoginRoute = location.pathname.endsWith('/login');

  // Promo banner on demo maps only: entice anonymous viewers to start a free
  // account. Hidden for the signed-in admin (!showAuthChrome) and in the admin
  // "preview as visitor" mode (!viewAsPublic) so we don't stack two banners.
  const showDemoPromo = isDemoAccount(userId) && !showAuthChrome && !viewAsPublic;

  // Login is a platform-brand moment: a clean white navbar with dark text
  // matches the landing page's chrome. On other routes, respect tenant
  // primaryColor, or fall back to the dark app nav.
  const navbarStyle = isLoginRoute
    ? { backgroundColor: '#ffffff', borderBottom: '1px solid rgba(15, 23, 42, 0.08)' }
    : company?.primaryColor
      ? { backgroundColor: company.primaryColor }
      : {};

  // On the dark app navbar we wrap the default logo in a white pill for contrast.
  // On the white login navbar the bare logo reads fine on its own.
  const defaultLogoImg = (
    <img
      src="/images/landing/default_logo.png"
      alt="Map of Projects logo"
      style={{ height: '36px', marginRight: '10px', display: 'block', flexShrink: 0 }}
    />
  );
  const defaultLogoPill = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
        borderRadius: '999px',
        padding: '4px 10px',
        marginRight: '10px',
        flexShrink: 0,
      }}
    >
      <img
        src="/images/landing/default_logo.png"
        alt="Map of Projects logo"
        style={{ height: '32px', display: 'block' }}
      />
    </span>
  );
  const defaultLogoEl = isLoginRoute ? defaultLogoImg : defaultLogoPill;

  return (
    // Wrapper holds the optional preview banner above the navbar so the
    // ResizeObserver measurement (--app-nav-height) covers both, keeping
    // MapView's height: calc(100dvh - var(--app-nav-height)) accurate.
    <div ref={navbarRef as React.RefObject<HTMLDivElement>} style={{ position: 'sticky', top: 0, zIndex: 1030 }}>
      {showDemoPromo && (
        <div
          role="region"
          aria-label="Promotion"
          style={{
            background: '#2563eb',
            color: '#ffffff',
            fontSize: '0.85rem',
            padding: '0.5rem 1rem',
            lineHeight: 1.4,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
          }}
        >
          <span>Like what you see? Build your own map free — 10 pins, no credit card required.</span>
          <Link
            to="/login?mode=register&source=demo-map-cta"
            style={{
              background: '#ffffff',
              color: '#1d4ed8',
              borderRadius: '999px',
              padding: '0.25rem 0.85rem',
              fontWeight: 600,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Start free →
          </Link>
        </div>
      )}
      {viewAsPublic && (
        <div
          role="status"
          style={{
            background: '#fffbeb',
            borderBottom: '1px solid #fde68a',
            color: '#78350f',
            fontSize: '0.85rem',
            padding: '0.5rem 1rem',
            textAlign: 'center',
            lineHeight: 1.4,
          }}
        >
          Previewing as visitor — admin links and login are hidden in this view.
          <button
            onClick={exitPreview}
            style={{
              background: 'none',
              border: 'none',
              textDecoration: 'underline',
              color: '#78350f',
              cursor: 'pointer',
              padding: 0,
              marginLeft: '0.5rem',
              fontSize: 'inherit',
              fontWeight: 600,
            }}
          >
            Exit preview
          </button>
        </div>
      )}
      <Navbar variant={isLoginRoute ? 'light' : 'dark'} expand style={navbarStyle} className={!isLoginRoute && !company?.primaryColor ? 'bg-dark' : ''}>
        <Container className="d-flex flex-column flex-md-row align-items-md-center">
          <div className="d-flex align-items-center justify-content-center justify-content-md-start w-100 w-md-auto" style={{ minWidth: 0 }}>
            {/* Company Logo — login is a platform-brand moment, always use default there */}
            {isLoginRoute ? defaultLogoEl : company?.logo ? (
              <img
                src={company.logo.url}
                alt={`${company.name} logo`}
                style={{ height: '40px', marginRight: '10px', flexShrink: 0 }}
              />
            ) : !company?.hideLogo && defaultLogoEl}

            {/* Company Name */}
            {company?.name && (
              <Navbar.Brand
                as={Link}
                to={userId ? `/${userId}` : "/"}
                className="me-md-3 text-truncate"
                style={{ maxWidth: '70vw' }}
              >
                {company.name}
              </Navbar.Brand>
            )}
          </div>

          <Nav className="flex-row align-items-center gap-2 flex-nowrap justify-content-center justify-content-md-end ms-md-auto mt-2 mt-md-0">
            {!location.pathname.endsWith('/login') && (
              <Nav.Link as={Link} to={mapTarget} onClick={handleViewFullMap} className="px-2 text-nowrap">View Full Map</Nav.Link>
            )}
            {showAuthChrome ? (
              <>
                <Nav.Link as={Link} to={userId ? `/${userId}/admin` : "/admin"} className="px-2 text-nowrap">Admin Dashboard</Nav.Link>
                <Button variant="outline-light" size="sm" onClick={handleLogout} className="text-nowrap">
                  Logout
                </Button>
              </>
            ) : !location.pathname.endsWith('/login') && !viewAsPublic && (
              <Nav.Link as={Link} to={userId ? `/${userId}/login` : "/login"} className="px-2 text-nowrap">Login / Register</Nav.Link>
            )}
          </Nav>
        </Container>
      </Navbar>
    </div>
  );
};

export default Navigation;